import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import Kpi from "../components/Kpi";
import Panel from "../components/Panel";
import { fmtNum, statusClass } from "../lib/format";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  AreaChart, Area,
} from "recharts";

function sanctionClass(s) {
  if (s === "Sanctioned") return "status-compliant";
  if (s === "Unsanctioned") return "status-at-risk";
  return "status-non-compliant"; // Blocked
}

function riskTone(r) {
  if (r === "Low") return "var(--cv-green)";
  if (r === "Medium") return "#E5C243";
  return "var(--cv-crimson)";
}

function severityTone(s) {
  if (s === "Critical") return "var(--cv-crimson)";
  if (s === "High") return "#D55816";
  if (s === "Medium") return "#E5C243";
  return "var(--cv-slate)";
}

export default function AISecurity() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    apiGet("/aisec/summary").then(setD);
  }, []);
  if (!d) return <div className="p-8 opacity-60">Loading…</div>;
  const k = d.kpis;
  const postureTone =
    d.posture_score >= 85 ? "var(--cv-green)"
      : d.posture_score >= 70 ? "#D55816"
        : "var(--cv-crimson)";

  return (
    <>
      <Masthead
        module="aisec"
        eyebrow="AI Security · GenAI · AI-SPM · Copilot Governance"
        title="AI Security Posture"
        subtitle="Shadow-AI discovery · prompt-DLP · prompt injection defence · model & pipeline posture · governance"
        meta={<div>Live · {d.kpis.prompts_scanned_30d.toLocaleString()} prompts scanned (30d)<br />as at {d.as_at}</div>}
      />
      <div className="px-[26px] py-5 flex-1">

        {/* Hero: composite posture + hero KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          <button
            type="button"
            onClick={() => nav("/drill/ai_governance_controls")}
            data-testid="aisec-posture"
            className="cv-panel text-left hover:shadow-lg transition-shadow"
            style={{ borderLeft: `4px solid ${postureTone}`, cursor: "pointer" }}
          >
            <div className="cv-kpi-label">AI Security Posture</div>
            <div className="flex items-end gap-3 mt-3">
              <div className="font-serif-display leading-none" style={{ color: postureTone, fontSize: 60 }}>
                {d.posture_score}
              </div>
              <div className="pb-2">
                <div className="text-[13px] small-caps font-bold" style={{ color: "var(--cv-charcoal-soft)" }}>/ 100</div>
                <div className="text-[11.5px]" style={{ color: "var(--cv-charcoal-soft)" }}>target {d.posture_target}</div>
              </div>
            </div>
            <div className="cv-kpi-sub mt-2">
              Composite of GenAI DLP, prompt-injection defence, AI-SPM &amp; governance framework coverage.
            </div>
          </button>
          <Kpi testid="aisec-apps" label="AI apps discovered" value={k.apps_discovered}
               sub={`${k.apps_sanctioned} sanctioned · ${k.apps_unsanctioned} unsanctioned · ${k.apps_blocked} blocked`}
               trend={{ delta_pct: +14, direction: "up", period: "MoM", is_improving: false }}
               onClick={() => nav("/drill/ai_apps")} />
          <Kpi testid="aisec-users" label="Active GenAI users" value={fmtNum(k.genai_users_active)}
               sub={`${k.genai_users_pct}% of workforce (${fmtNum(k.workforce_total)})`}
               trend={{ delta_pct: +18, direction: "up", period: "MoM", is_improving: true }}
               onClick={() => nav("/drill/ai_dlp_events")} />
          <Kpi testid="aisec-blocked" label="Sensitive prompts blocked (30d)" value={fmtNum(k.sensitive_prompts_blocked_30d)}
               sub={`${fmtNum(k.prompts_scanned_30d)} prompts inspected`}
               alert
               trend={{ delta_pct: +6, direction: "up", period: "MoM", is_improving: true }}
               onClick={() => nav("/drill/ai_dlp_events?disposition=Blocked")} />
        </div>

        {/* Second row — Data Protection for AI */}
        <div className="cv-section-label">Data Protection for AI · Prompt-DLP</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <Kpi testid="aisec-pii" label="PII in prompts blocked" value={fmtNum(528)} sub="last 30 days" alert
               onClick={() => nav("/drill/ai_dlp_events?category_key=pii&disposition=Blocked")} />
          <Kpi testid="aisec-code" label="Source code exfil blocked" value={fmtNum(214)} sub="last 30 days"
               trend={{ delta_pct: +11, direction: "up", period: "MoM", is_improving: true }}
               onClick={() => nav("/drill/ai_dlp_events?category_key=source_code&disposition=Blocked")} />
          <Kpi testid="aisec-creds" label="Credentials · secrets blocked" value={fmtNum(82)} sub="last 30 days"
               trend={{ delta_pct: -3, direction: "down", period: "MoM", is_improving: false }}
               onClick={() => nav("/drill/ai_dlp_events?category_key=credentials&disposition=Blocked")} />
          <Kpi testid="aisec-files" label="File uploads blocked to AI apps" value={fmtNum(k.files_blocked_30d)} sub="last 30 days"
               onClick={() => nav("/drill/ai_dlp_events?disposition=Blocked")} />
        </div>

        {/* Threats & Prompt Volume charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <Panel testid="panel-ai-threats" title="Prompt Injection &amp; Jailbreak (24h)"
                 subtitle="Detected across sanctioned GenAI apps and internal LLM endpoints">
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button type="button" data-testid="aithreat-direct" onClick={() => nav("/drill/ai_prompt_threats?kind=Direct%20prompt%20injection")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Direct prompt injection</div>
                <div className="cv-tile-value" style={{ color: "var(--cv-crimson)" }}>{d.threats.prompt_injection_direct}</div>
              </button>
              <button type="button" data-testid="aithreat-indirect" onClick={() => nav("/drill/ai_prompt_threats?kind=Indirect%20%C2%B7%20RAG%20poisoning")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Indirect · RAG poisoning</div>
                <div className="cv-tile-value" style={{ color: "var(--cv-crimson)" }}>{d.threats.prompt_injection_indirect}</div>
              </button>
              <button type="button" data-testid="aithreat-jailbreak" onClick={() => nav("/drill/ai_prompt_threats?kind=Jailbreak%20%C2%B7%20DAN%20%2F%20roleplay")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Jailbreak (DAN, roleplay)</div>
                <div className="cv-tile-value" style={{ color: "#D55816" }}>{d.threats.jailbreak_dan_roleplay}</div>
              </button>
              <button type="button" data-testid="aithreat-dos" onClick={() => nav("/drill/ai_prompt_threats?kind=Model%20DoS%20%C2%B7%20prompt-flood")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Model DoS / prompt-flood</div>
                <div className="cv-tile-value" style={{ color: "#D55816" }}>{d.threats.model_dos_attempts}</div>
              </button>
              <button type="button" data-testid="aithreat-poison" onClick={() => nav("/drill/ai_prompt_threats?kind=Data%20%2F%20training%20poisoning")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Data / training poisoning</div>
                <div className="cv-tile-value" style={{ color: "#E5C243" }}>{d.threats.data_poisoning_alerts}</div>
              </button>
              <button type="button" data-testid="aithreat-malout" onClick={() => nav("/drill/ai_prompt_threats?kind=Malicious%20output%20flagged")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Malicious output flagged</div>
                <div className="cv-tile-value" style={{ color: "#E5C243" }}>{d.threats.malicious_output_flagged}</div>
              </button>
            </div>
            <div className="h-[110px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.threats.series_24h} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                  <CartesianGrid stroke="var(--cv-line)" strokeDasharray="3 4" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fill: "var(--cv-dark-grey)", fontSize: 10 }} stroke="var(--cv-line)" />
                  <YAxis tick={{ fill: "var(--cv-dark-grey)", fontSize: 10 }} stroke="var(--cv-line)" />
                  <Tooltip cursor={{ fill: "rgba(213,88,22,0.06)" }} />
                  <Bar dataKey="injection" fill="var(--cv-crimson)" name="Injection" />
                  <Bar dataKey="jailbreak" fill="#D55816" name="Jailbreak" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel testid="panel-prompt-volume" title="Prompt Volume (30d)"
                 subtitle="Allowed vs blocked across all sanctioned GenAI apps"
                 unit="count" current={k.prompts_scanned_30d}>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.prompt_volume_30d} margin={{ top: 5, right: 8, bottom: 0, left: -20 }}>
                  <defs>
                    <linearGradient id="allowedFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--cv-slate)" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="var(--cv-slate)" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="blockedFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--cv-crimson)" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="var(--cv-crimson)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="var(--cv-line)" strokeDasharray="3 4" vertical={false} />
                  <XAxis dataKey="day" tick={{ fill: "var(--cv-dark-grey)", fontSize: 10 }} stroke="var(--cv-line)" minTickGap={20} />
                  <YAxis tick={{ fill: "var(--cv-dark-grey)", fontSize: 10 }} stroke="var(--cv-line)" />
                  <Tooltip />
                  <Area type="monotone" dataKey="allowed" stroke="var(--cv-slate)" fill="url(#allowedFill)" name="Allowed" strokeWidth={1.8} />
                  <Area type="monotone" dataKey="blocked" stroke="var(--cv-crimson)" fill="url(#blockedFill)" name="Blocked" strokeWidth={1.8} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[11.5px] mt-2" style={{ color: "var(--cv-charcoal-soft)" }}>
              Red band = policy-blocked prompts · Slate band = allowed after scan
            </div>
          </Panel>

          <Panel testid="panel-dlp" title="Prompt-DLP by Category"
                 subtitle="What kind of sensitive data users try to send to GenAI">
            <div className="flex flex-col gap-2">
              {d.dlp_categories.map((c) => {
                const max = Math.max(...d.dlp_categories.map(x => x.blocked));
                const pct = Math.round((c.blocked / max) * 100);
                const trendTone = c.trend_pct >= 0 ? "var(--cv-crimson)" : "var(--cv-green)";
                return (
                  <button
                    type="button"
                    key={c.category}
                    data-testid={`dlp-cat-${c.key}`}
                    onClick={() => nav(`/drill/ai_dlp_events?category_key=${c.key}`)}
                    className="text-left w-full py-1 hover:opacity-80 transition-opacity"
                    style={{ background: "transparent", border: 0, cursor: "pointer" }}
                  >
                    <div className="flex justify-between items-center text-[13px] mb-0.5">
                      <span className="truncate pr-2" style={{ color: "var(--cv-charcoal)" }}>{c.category}</span>
                      <span className="tabular-nums font-bold" style={{ color: "var(--cv-charcoal)" }}>
                        {fmtNum(c.blocked)} <span className="text-[11px] font-normal" style={{ color: trendTone }}>({c.trend_pct >= 0 ? "+" : ""}{c.trend_pct}%)</span>
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--cv-line)" }}>
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, var(--cv-crimson), #D55816)" }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </Panel>
        </div>

        {/* App catalog + AI-SPM Models */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <Panel testid="panel-app-catalog" title="GenAI App Catalog"
                 subtitle={`${d.app_catalog.length} apps · sanctioned · unsanctioned · blocked`}>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left small-caps" style={{ color: "var(--cv-dark-grey)", borderBottom: "1px solid var(--cv-line)" }}>
                    <th className="py-2 px-2 font-bold">App</th>
                    <th className="py-2 px-2 font-bold">Category</th>
                    <th className="py-2 px-2 font-bold">Status</th>
                    <th className="py-2 px-2 font-bold text-right">Users</th>
                    <th className="py-2 px-2 font-bold text-right">Prompts / day</th>
                    <th className="py-2 px-2 font-bold text-right">Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {d.app_catalog.map((a) => (
                    <tr key={a.app} data-testid={`app-row-${a.key}`}
                        onClick={() => nav(`/drill/ai_dlp_events?app_key=${a.key}`)}
                        className="hover:bg-orange-50 cursor-pointer transition-colors"
                        style={{ borderBottom: "1px solid var(--cv-line)" }}>
                      <td className="py-2 px-2 font-bold" style={{ color: "var(--cv-charcoal)" }}>{a.app}</td>
                      <td className="py-2 px-2" style={{ color: "var(--cv-charcoal-soft)" }}>{a.category}</td>
                      <td className="py-2 px-2"><span className={`status-pill ${sanctionClass(a.sanction)}`}>{a.sanction}</span></td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtNum(a.users)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtNum(a.prompts_day)}</td>
                      <td className="py-2 px-2 text-right font-bold" style={{ color: riskTone(a.risk) }}>{a.risk}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel testid="panel-models" title="AI-SPM · Model Inventory &amp; Risk"
                 subtitle={`${d.models.total} managed models across cloud AI platforms`}
                 unit="count" current={d.models.total}>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {d.models.by_platform.map((p) => (
                <button type="button" key={p.platform}
                        data-testid={`model-platform-${p.platform.replace(/\s+/g,'-').toLowerCase()}`}
                        onClick={() => nav(`/drill/ai_models?platform=${encodeURIComponent(p.platform)}`)}
                        className="cv-tile text-left hover:shadow-md transition-shadow"
                        style={{ cursor: "pointer" }}>
                  <div className="cv-tile-label">{p.platform}</div>
                  <div className="flex items-end gap-1">
                    <div className="cv-tile-value">{p.count}</div>
                    {p.critical > 0 && (
                      <div className="text-[11px] font-bold pb-1" style={{ color: "var(--cv-crimson)" }}>
                        {p.critical} crit
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button type="button" data-testid="model-exposed" onClick={() => nav("/drill/ai_models?severity=Critical")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Public endpoints</div>
                <div className="cv-tile-value" style={{ color: "var(--cv-crimson)" }}>{d.models.exposed_public_endpoints}</div>
              </button>
              <button type="button" data-testid="model-buckets" onClick={() => nav("/drill/ai_models?severity=Critical")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Training bkts exposed</div>
                <div className="cv-tile-value" style={{ color: "var(--cv-crimson)" }}>{d.models.training_buckets_exposed}</div>
              </button>
              <button type="button" data-testid="model-cves" onClick={() => nav("/drill/ai_models")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}>
                <div className="cv-tile-label">Pipeline CVEs</div>
                <div className="cv-tile-value" style={{ color: "#D55816" }}>{d.models.pipeline_cves.total}</div>
              </button>
            </div>
            <div className="text-[11.5px] small-caps font-bold tracking-[0.1em] mb-1" style={{ color: "var(--cv-dark-grey)" }}>
              Top model risks
            </div>
            <div className="flex flex-col gap-1.5">
              {d.models.top_risks.map((r) => (
                <button type="button" key={r.model}
                        data-testid={`model-risk-${r.model}`}
                        onClick={() => nav(`/drill/ai_models?q=${encodeURIComponent(r.model)}`)}
                        className="flex justify-between items-start gap-2 text-[12.5px] py-1.5 text-left hover:opacity-80 transition-opacity"
                        style={{ borderBottom: "1px dashed var(--cv-line)", background: "transparent", border: 0, cursor: "pointer", borderBottomStyle: "dashed", borderBottomColor: "var(--cv-line)", borderBottomWidth: "1px" }}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate" style={{ color: "var(--cv-charcoal)" }}>{r.model}</div>
                    <div className="text-[11px]" style={{ color: "var(--cv-charcoal-soft)" }}>{r.platform} · {r.risk}</div>
                  </div>
                  <span className="text-[11px] font-bold small-caps shrink-0" style={{ color: severityTone(r.severity) }}>{r.severity}</span>
                </button>
              ))}
            </div>
          </Panel>

          <Panel testid="panel-copilot" title="Copilot Governance"
                 subtitle="M365 Copilot · GitHub Copilot · sensitive data exposure">
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button type="button" onClick={() => nav("/drill/ai_copilot_flags?surface=M365%20Copilot")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}><div className="cv-tile-label">M365 licensed</div><div className="cv-tile-value">{fmtNum(d.copilot.m365_licensed)}</div></button>
              <button type="button" onClick={() => nav("/drill/ai_copilot_flags?surface=M365%20Copilot")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}><div className="cv-tile-label">M365 active (30d)</div><div className="cv-tile-value">{fmtNum(d.copilot.m365_active_30d)}</div></button>
              <button type="button" onClick={() => nav("/drill/ai_copilot_flags?surface=GitHub%20Copilot")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}><div className="cv-tile-label">GitHub Copilot active</div><div className="cv-tile-value">{fmtNum(d.copilot.github_active)}</div></button>
              <button type="button" onClick={() => nav("/drill/ai_copilot_flags?severity=High")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}><div className="cv-tile-label">Sensitive queries flagged</div><div className="cv-tile-value" style={{ color: "var(--cv-crimson)" }}>{fmtNum(d.copilot.sensitive_queries_flagged_30d)}</div></button>
              <button type="button" onClick={() => nav("/drill/ai_copilot_flags")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}><div className="cv-tile-label">Oversharing sites</div><div className="cv-tile-value" style={{ color: "#D55816" }}>{fmtNum(d.copilot.oversharing_sites_flagged)}</div></button>
              <button type="button" onClick={() => nav("/drill/ai_copilot_flags")} className="cv-tile text-left hover:shadow-md transition-shadow" style={{ cursor: "pointer" }}><div className="cv-tile-label">Audit events/day</div><div className="cv-tile-value">{fmtNum(d.copilot.audit_events_ingested_day)}</div></button>
            </div>
            <div className="text-[11.5px] small-caps font-bold tracking-[0.1em] mb-1" style={{ color: "var(--cv-dark-grey)" }}>
              Top governance flags
            </div>
            <div className="flex flex-col gap-1.5">
              {d.copilot.top_flags.map((f) => (
                <button type="button" key={f.user_group + f.flag}
                        onClick={() => nav(`/drill/ai_copilot_flags?user_group=${encodeURIComponent(f.user_group)}`)}
                        className="flex justify-between items-start gap-2 text-[12.5px] py-1.5 text-left hover:opacity-80 transition-opacity w-full"
                        style={{ background: "transparent", border: 0, borderBottom: "1px dashed var(--cv-line)", cursor: "pointer" }}>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold" style={{ color: "var(--cv-charcoal)" }}>{f.user_group}</div>
                    <div className="text-[11px]" style={{ color: "var(--cv-charcoal-soft)" }}>{f.flag}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="tabular-nums font-bold" style={{ color: "var(--cv-charcoal)" }}>{f.count}</div>
                    <span className="text-[10.5px] small-caps font-bold" style={{ color: severityTone(f.severity) }}>{f.severity}</span>
                  </div>
                </button>
              ))}
            </div>
          </Panel>
        </div>

        {/* Governance frameworks */}
        <div className="mb-5">
          <Panel testid="panel-governance" title="AI Governance &amp; Compliance Frameworks"
                 subtitle="Control coverage against leading AI governance standards"
                 unit="percent"
                 current={Math.round(d.governance.frameworks.reduce((s, f) => s + f.coverage, 0) / d.governance.frameworks.length)}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              {d.governance.frameworks.map((f) => {
                const tone = f.coverage >= 85 ? "var(--cv-green)" : f.coverage >= 65 ? "#D55816" : "var(--cv-crimson)";
                return (
                  <button type="button" key={f.name}
                          data-testid={`framework-${f.key}`}
                          onClick={() => nav(`/drill/ai_governance_controls?framework_key=${f.key}`)}
                          className="rounded-xl p-3 text-left hover:shadow-md transition-shadow"
                          style={{ background: "var(--cv-sand)", border: "1px solid var(--cv-line)", cursor: "pointer" }}>
                    <div className="text-[12px] font-bold small-caps tracking-[0.08em] leading-snug min-h-[32px]" style={{ color: "var(--cv-charcoal)" }}>
                      {f.name}
                    </div>
                    <div className="flex items-end gap-2 mt-2">
                      <div className="font-serif-display leading-none text-[30px]" style={{ color: tone }}>{f.coverage}%</div>
                      <div className="text-[11px] mb-1" style={{ color: "var(--cv-charcoal-soft)" }}>target {f.target}%</div>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(50,50,50,0.08)" }}>
                      <div className="h-full rounded-full" style={{ width: `${f.coverage}%`, background: tone }} />
                    </div>
                    <div className="text-[11.5px] mt-1.5" style={{ color: "var(--cv-charcoal-soft)" }}>
                      {f.controls_met} of {f.controls_total} controls met
                    </div>
                  </button>
                );
              })}
            </div>
          </Panel>
        </div>
      </div>
    </>
  );
}
